"use client"
import dynamic from "next/dynamic"
import { Suspense } from "react"
import { Blog } from "@src/types/blog"
import { Stack, Skeleton, Grid, Box } from "@mui/material"
import { useTranslation } from "react-i18next"

// Skeleton component for loading state
const BlogSkeleton = () => {
  return (
    <Stack sx={{ width: "100%", display: "flex", "flexDirection": "column" }}>
      <Skeleton variant="text" width={200} height={36} />
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {[1, 2, 3].map((item) => (
          <Grid key={item} item xs={12} md={6} lg={4}>
            <Stack
              gap={2}
              flexDirection="column"
              sx={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "24px",
                height: "100%"
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "16/9",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "16px"
                  }}
                />
              </Box>

              <Box>
                <Skeleton 
                  variant="text" 
                  height={72} 
                  sx={{ 
                    minHeight: "72px",
                    maxHeight: "72px"
                  }}
                />
                <Skeleton variant="text" height={24} width="60%" />
                
                <Stack
                  sx={{
                    height: {
                      xs: "auto",
                      md: "30px"
                    }
                  }}
                  mt={4}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack gap="8px" direction="row">
                    <Skeleton variant="text" width={60} height={20} />
                    <Skeleton variant="text" width={50} height={20} />
                  </Stack>
                  <Skeleton variant="circular" width={24} height={24} />
                </Stack>
              </Box>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Stack>
  )
}

// Code-split import, client-only
const LazySection = dynamic(() => import("@src/sections/blog/relative-blog"), {
  loading: () => <BlogSkeleton />,
  ssr: false
})

interface Props {
  parentUrl: string
  title?: string
  data: Blog[]
}

export default function LazyRelativeBlog({
  parentUrl,
  title,
  data = []
}: Props) {
  const { t } = useTranslation()
  const resolvedTitle = title ?? t("blog")

  // Mounts unconditionally (no IntersectionObserver gating): gating a `dynamic(..., { ssr: false })`
  // import behind a late state change previously left it stuck in its loading fallback forever
  // (same failure mode fixed for the footer in `layouts/footer/lazy-footer.tsx`).
  return (
    <Suspense fallback={<BlogSkeleton />}>
      <LazySection parentUrl={parentUrl} title={resolvedTitle} data={data} />
    </Suspense>
  )
}
